// Public page: Consent to Treatment & Release of Information (On-Site School
// Services). Reached from a tokened link the therapist sends
// (/school-consent?token=...). Modeled on ROIFormPage.jsx, minus the
// localhost bypass. One scrolling page in the paper form's order.

import React, { useState, useRef, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { todayEastern, formatDate } from '../dateUtils';
import {
  SCHOOL_CONSENT_TITLE,
  HEADER_LABELS,
  withSchool,
} from '../data/schoolConsentText';
import { buildSchoolConsentPrintHtml, openPrintWindow } from './SchoolConsentComponents';

const SIG_W = 400;
const SIG_H = 120;

export default function SchoolConsentFormPage() {
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);

  const [client, setClient] = useState({ firstName: '', lastName: '', dob: '', isMinor: false });
  const [schoolName, setSchoolName] = useState('');
  const [therapistName, setTherapistName] = useState('');

  const today = todayEastern();
  const [form, setForm] = useState({
    gradeTeacher: '',
    releaseAuthorized: null, // true | false | null (unanswered)
    releasePersonnel: '',
    shareAttendance: false,
    shareProgressGoals: false,
    shareClassroomStrategies: false,
    shareOther: false,
    shareOtherSpecify: '',
    releaseExpiration: '',
    ackPrintedName: '',
    ackSignatureDate: today,
  });
  // Fields the therapist prefilled; locked for the parent unless edits are allowed.
  const [prefilled, setPrefilled] = useState({});
  const [allowEdits, setAllowEdits] = useState(true);

  // One signature (section 6) covers sections 4–6.
  const [ackSig, setAckSig] = useState(null);
  const ackSigRef = useRef(null);

  const [errors, setErrors] = useState({});

  // Token validation
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token');
    if (!t) {
      setErrorMsg('No token provided');
      setLoading(false);
      return;
    }
    setToken(t);

    fetch(`/api/forms/school-consent/validate?token=${encodeURIComponent(t)}`)
      .then(async (res) => {
        let body = {};
        try { body = await res.json(); } catch { /* non-JSON error */ }
        if (res.ok && body.valid) {
          const c = body.client || {};
          setClient({
            firstName: c.firstName || '',
            lastName: c.lastName || '',
            dob: c.dob ? String(c.dob).split('T')[0] : '',
            isMinor: !!c.isMinor,
          });
          setSchoolName(body.schoolName || '');
          setTherapistName(body.therapistName || '');

          // Therapist prefill (same idea as the ROI form's preFilledData).
          const pf = body.preFilledData || {};
          setAllowEdits(pf.allowClientEdits !== false);
          const filled = {};
          if (pf.gradeTeacher) filled.gradeTeacher = String(pf.gradeTeacher);
          if (pf.releaseAuthorized === true || pf.releaseAuthorized === false) filled.releaseAuthorized = pf.releaseAuthorized;
          if (pf.releasePersonnel) filled.releasePersonnel = String(pf.releasePersonnel);
          ['shareAttendance', 'shareProgressGoals', 'shareClassroomStrategies', 'shareOther'].forEach((k) => {
            if (pf[k] === true) filled[k] = true;
          });
          if (pf.shareOtherSpecify) { filled.shareOtherSpecify = String(pf.shareOtherSpecify); filled.shareOther = true; }
          if (pf.releaseExpiration) filled.releaseExpiration = String(pf.releaseExpiration);
          setPrefilled(filled);
          if (Object.keys(filled).length) setForm(prev => ({ ...prev, ...filled }));
        } else {
          setErrorMsg(body.error || 'This link is invalid, has expired, or has already been used.');
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error('Validation error:', error);
        setErrorMsg('Error validating link');
        setLoading(false);
      });
  }, []);

  const clientName = `${client.firstName} ${client.lastName}`.trim();
  const t = withSchool(schoolName);

  const setField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const setRelease = (value) => {
    setForm(prev => ({ ...prev, releaseAuthorized: value }));
    setErrors(prev => ({ ...prev, releaseAuthorized: undefined }));
  };

  // A prefilled field is read-only when the therapist didn't allow edits.
  const locked = (field) => !allowEdits && prefilled[field] !== undefined;
  const releaseLocked = !allowEdits && prefilled.releaseAuthorized !== undefined;

  // Signature pads
  const captureSig = (ref, setter, errKey) => {
    if (ref.current && !ref.current.isEmpty()) {
      setter(ref.current.toDataURL('image/png'));
      setErrors(prev => (prev[errKey] ? { ...prev, [errKey]: undefined } : prev));
    } else {
      setter(null);
    }
  };
  const clearSig = (ref, setter) => {
    if (ref.current) ref.current.clear();
    setter(null);
  };

  // Client-side validation mirroring the server's rules
  const validate = () => {
    const e = {};
    const yes = form.releaseAuthorized === true;
    const dateCheck = (key, value, label) => {
      if (!value) e[key] = `Please enter the ${label}.`;
      else if (value > today) e[key] = `The ${label} cannot be in the future.`;
    };

    if (form.releaseAuthorized !== true && form.releaseAuthorized !== false) {
      e.releaseAuthorized = 'Please choose Yes or No.';
    }
    if (yes) {
      if (!form.releasePersonnel.trim()) e.releasePersonnel = 'Please list the authorized school personnel.';
      const anyShare = form.shareAttendance || form.shareProgressGoals || form.shareClassroomStrategies || form.shareOther;
      if (!anyShare) e.shareOptions = 'Please choose at least one type of information to share.';
      if (form.shareOther && !form.shareOtherSpecify.trim()) e.shareOtherSpecify = 'Please specify the other information.';
    }

    if (!ackSig) e.ackSig = 'Please sign to acknowledge and accept.';
    if (!form.ackPrintedName.trim()) e.ackPrintedName = 'Please print your name.';
    dateCheck('ackSignatureDate', form.ackSignatureDate, 'date signed');

    return e;
  };

  const handleSubmit = () => {
    const e = validate();
    setErrors(e);
    const firstKey = Object.keys(e).find(k => e[k]);
    if (firstKey) {
      const el = document.getElementById(`sc-${firstKey}`);
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const yes = form.releaseAuthorized === true;
    const data = {
      clientName,
      clientDob: client.dob,
      schoolName,
      gradeTeacher: form.gradeTeacher.trim(),
      releaseAuthorized: yes,
      releasePersonnel: yes ? form.releasePersonnel.trim() : '',
      shareAttendance: yes && form.shareAttendance,
      shareProgressGoals: yes && form.shareProgressGoals,
      shareClassroomStrategies: yes && form.shareClassroomStrategies,
      shareOther: yes && form.shareOther,
      shareOtherSpecify: yes && form.shareOther ? form.shareOtherSpecify.trim() : '',
      releaseExpiration: yes ? form.releaseExpiration.trim() : '',
      ackSignatureData: ackSig,
      ackPrintedName: form.ackPrintedName.trim(),
      ackSignatureDate: form.ackSignatureDate,
    };

    setSubmitting(true);
    setSubmitError('');
    fetch('/api/forms/school-consent/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, data }),
    })
      .then(async (res) => {
        let body = {};
        try { body = await res.json(); } catch { /* non-JSON */ }
        if (!res.ok || body.success === false || body.error) {
          throw new Error(body.error || `Server error (${res.status})`);
        }
        setSubmittedData(data);
        setSubmitted(true);
      })
      .catch((error) => {
        console.error('Error submitting school consent form:', error);
        setSubmitError(error.message || 'Error submitting the form. Please try again.');
      })
      .finally(() => setSubmitting(false));
  };

  const handlePrint = () => {
    if (!submittedData) return;
    openPrintWindow(buildSchoolConsentPrintHtml(submittedData, { schoolName, submittedAt: new Date().toISOString() }));
  };

  // ── render states ──────────────────────────────────────────────────────────
  if (loading) {
    return <div style={styles.centered}><p>Loading...</p></div>;
  }
  if (errorMsg) {
    return (
      <div style={styles.centered}>
        <p style={styles.errorMessage}>{errorMsg}</p>
        <button onClick={() => window.location.reload()} style={styles.retryButton}>Try Again</button>
      </div>
    );
  }
  if (submitted) {
    return (
      <div style={styles.centered}>
        <h2>Thank you!</h2>
        <p>Your consent form for {clientName} has been submitted successfully.</p>
        <p>The completed form has been sent to {therapistName || 'your child\'s therapist'}.</p>
        <button onClick={handlePrint} style={styles.printButton}>Print Copy</button>
      </div>
    );
  }

  const Err = ({ k }) => (errors[k] ? <div id={`sc-${k}`} style={styles.errorText}>{errors[k]}</div> : null);

  const yes = form.releaseAuthorized === true;

  return (
    <div className="roi-container" style={styles.container}>
      <div style={styles.header}>
        <img src="/letterhead.png" alt="Avenues Unlimited Counseling Center" style={styles.letterheadImg} />
      </div>

      <h2 style={styles.formTitle}>{SCHOOL_CONSENT_TITLE}</h2>

      {/* Header fields */}
      <div style={styles.section}>
        <div style={styles.row}>
          <div style={styles.group}>
            <label style={styles.label}>{HEADER_LABELS.clientName}:</label>
            <input type="text" value={clientName} readOnly style={{ ...styles.input, ...styles.readonly }} />
          </div>
          <div style={styles.group}>
            <label style={styles.label}>{HEADER_LABELS.clientDob}:</label>
            <input type="text" value={formatDate(client.dob)} readOnly style={{ ...styles.input, ...styles.readonly }} />
          </div>
        </div>
        <div style={styles.row}>
          <div style={styles.group}>
            <label style={styles.label}>{HEADER_LABELS.schoolName}:</label>
            <input type="text" value={schoolName} readOnly style={{ ...styles.input, ...styles.readonly }} />
          </div>
          <div style={styles.group}>
            <label style={styles.label}>{HEADER_LABELS.gradeTeacher}:</label>
            <input
              type="text"
              value={form.gradeTeacher}
              onChange={e => setField('gradeTeacher', e.target.value)}
              readOnly={locked('gradeTeacher')}
              style={{ ...styles.input, ...(locked('gradeTeacher') ? styles.readonly : {}) }}
              placeholder="e.g., 3rd grade / Mrs. Smith"
            />
          </div>
        </div>
      </div>

      {/* 1. Purpose */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>{t.purpose.title}</h3>
        <p style={styles.p}>{t.purpose.body}</p>
      </div>

      {/* 2. Services */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>{t.services.title}</h3>
        <ul style={styles.ul}>
          {t.services.bullets.map(b => <li key={b.lead}><strong>{b.lead}:</strong> {b.text}</li>)}
        </ul>
      </div>

      {/* 3. Confidentiality */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>{t.confidentiality.title}</h3>
        <p style={styles.p}>{t.confidentiality.intro}</p>
        <ul style={styles.ul}>{t.confidentiality.bullets.map(b => <li key={b}>{b}</li>)}</ul>
      </div>

      {/* 4. Consent to Treatment */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>{t.consent.title}</h3>
        <p style={styles.p}>{t.consent.intro}</p>
        <ul style={styles.ul}>{t.consent.bullets.map(b => <li key={b}>{b}</li>)}</ul>
      </div>

      {/* 5. Release of Information */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>{t.release.title}</h3>
        <p style={{ ...styles.p, fontStyle: 'italic' }}>{t.release.checkOne}</p>
        {releaseLocked && <div style={styles.hint}>This choice was filled in by your child's therapist.</div>}

        <label style={{ ...styles.choice, ...(yes ? styles.choiceSelected : {}) }}>
          <input
            type="radio"
            name="releaseAuthorized"
            checked={yes}
            disabled={releaseLocked}
            onChange={() => setRelease(true)}
            style={styles.radio}
          />
          <span><strong>{t.release.yesLead}</strong> – {t.release.yesText}</span>
        </label>

        {yes && (
          <div style={styles.releaseDetails}>
            <div style={styles.group}>
              <label style={styles.label}>{t.release.personnelLabel} *</label>
              <input
                type="text"
                value={form.releasePersonnel}
                onChange={e => setField('releasePersonnel', e.target.value)}
                readOnly={locked('releasePersonnel')}
                style={{ ...styles.input, ...(locked('releasePersonnel') ? styles.readonly : {}) }}
                placeholder="Names and/or roles (e.g., school counselor, homeroom teacher)"
              />
              <Err k="releasePersonnel" />
            </div>

            <label style={styles.label}>{t.release.sharedHeading} *</label>
            <div style={styles.checkboxList}>
              {t.release.shareOptions.map(o => (
                <label key={o.key} style={styles.checkboxItem}>
                  <input
                    type="checkbox"
                    checked={!!form[o.key]}
                    disabled={locked(o.key)}
                    onChange={e => { setField(o.key, e.target.checked); setErrors(prev => ({ ...prev, shareOptions: undefined })); }}
                    style={styles.checkbox}
                  />
                  <span style={styles.checkboxText}>{o.label}</span>
                </label>
              ))}
              <div style={{ ...styles.checkboxItem, flexDirection: 'column', alignItems: 'stretch', cursor: 'default' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={form.shareOther}
                    disabled={locked('shareOther')}
                    onChange={e => { setField('shareOther', e.target.checked); setErrors(prev => ({ ...prev, shareOptions: undefined })); }}
                    style={styles.checkbox}
                  />
                  <span style={styles.checkboxText}>{t.release.otherLabel}:</span>
                </label>
                <input
                  type="text"
                  value={form.shareOtherSpecify}
                  onChange={e => { setField('shareOther', true); setField('shareOtherSpecify', e.target.value); setErrors(prev => ({ ...prev, shareOptions: undefined })); }}
                  readOnly={locked('shareOtherSpecify')}
                  style={{ ...styles.input, marginTop: 8, ...(locked('shareOtherSpecify') ? styles.readonly : {}) }}
                  placeholder="Describe the other information to be shared"
                />
                <Err k="shareOtherSpecify" />
              </div>
            </div>
            <Err k="shareOptions" />

            <div style={styles.group}>
              <label style={styles.label}>{t.release.expirationLead} <span style={styles.hint}>{t.release.expirationHint}</span></label>
              <input
                type="text"
                value={form.releaseExpiration}
                onChange={e => setField('releaseExpiration', e.target.value)}
                readOnly={locked('releaseExpiration')}
                style={{ ...styles.input, ...(locked('releaseExpiration') ? styles.readonly : {}) }}
                placeholder="e.g., 06/30/2027 or end of the school year"
              />
              <div style={styles.hint}>{t.release.expirationTail}</div>
            </div>
          </div>
        )}

        <label style={{ ...styles.choice, ...(form.releaseAuthorized === false ? styles.choiceSelected : {}) }}>
          <input
            type="radio"
            name="releaseAuthorized"
            checked={form.releaseAuthorized === false}
            disabled={releaseLocked}
            onChange={() => setRelease(false)}
            style={styles.radio}
          />
          <span><strong>{t.release.noLead}</strong> – {t.release.noText}</span>
        </label>
        <Err k="releaseAuthorized" />
      </div>

      {/* 6. Acknowledgment */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>{t.acknowledgment.title}</h3>
        <p style={styles.p}>{t.acknowledgment.body}</p>

        <div style={styles.signatureSection}>
          <label style={styles.signatureLabel}>{t.acknowledgment.signatureLabel} *</label>
          <div style={styles.signatureContainer}>
            <SignatureCanvas
              ref={ackSigRef}
              penColor="black"
              canvasProps={{ width: SIG_W, height: SIG_H, style: styles.signatureCanvas }}
              onEnd={() => captureSig(ackSigRef, setAckSig, 'ackSig')}
            />
            <div style={styles.signatureInstructions}>Please sign above</div>
          </div>
          <div>
            <button type="button" onClick={() => clearSig(ackSigRef, setAckSig)} style={styles.clearButton}>Clear Signature</button>
          </div>
          <Err k="ackSig" />

          <div style={styles.row}>
            <div style={styles.group}>
              <label style={styles.label}>{t.acknowledgment.printedNameLabel} *</label>
              <input
                type="text"
                value={form.ackPrintedName}
                onChange={e => setField('ackPrintedName', e.target.value)}
                style={styles.input}
              />
              <Err k="ackPrintedName" />
            </div>
            <div style={styles.group}>
              <label style={styles.label}>{t.acknowledgment.dateLabel} *</label>
              <input
                type="date"
                value={form.ackSignatureDate}
                max={today}
                onChange={e => setField('ackSignatureDate', e.target.value)}
                style={styles.input}
              />
              <Err k="ackSignatureDate" />
            </div>
          </div>
        </div>
        <div style={styles.hint}>Dates default to today. If you signed on an earlier date, enter that date. Dates cannot be in the future.</div>
      </div>

      {submitError && <div style={styles.submitError}>{submitError}</div>}
      {Object.keys(errors).some(k => errors[k]) && (
        <div style={styles.submitError}>Please complete the highlighted items above.</div>
      )}

      <div style={styles.navigationButtons}>
        <button onClick={handleSubmit} disabled={submitting} style={{ ...styles.submitButton, opacity: submitting ? 0.7 : 1 }}>
          {submitting ? 'Submitting…' : 'Submit Consent Form'}
        </button>
      </div>

      <div style={styles.footer}>
        <img src="/footer.png" alt="" style={styles.footerImg} />
      </div>
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '20px',
    fontFamily: "'Times New Roman', Times, serif",
    lineHeight: '1.5',
    color: 'var(--text-strong)',
    background: '#fff',
  },
  header: { textAlign: 'center', marginBottom: '24px' },
  letterheadImg: { width: '280px', maxWidth: '70%', height: 'auto' },
  footer: { textAlign: 'center', marginTop: '40px', marginBottom: '20px' },
  footerImg: { maxWidth: '100%', height: 'auto' },
  formTitle: {
    textAlign: 'center',
    color: 'var(--primary-color)',
    fontSize: '18px',
    fontWeight: 'bold',
    marginBottom: '24px',
    padding: '12px 15px',
    border: '2px solid var(--primary-color)',
    background: 'var(--surface-muted)',
  },
  section: {
    marginBottom: '24px',
    border: '1px solid var(--border-color)',
    padding: '18px 20px',
    borderRadius: '5px',
    background: 'var(--surface-muted)',
  },
  sectionTitle: {
    marginTop: 0,
    marginBottom: '12px',
    color: 'var(--primary-color)',
    fontSize: '16px',
    fontWeight: 'bold',
    borderBottom: '1px solid var(--border-color)',
    paddingBottom: '8px',
  },
  p: { margin: '0 0 10px', fontSize: '14px' },
  ul: { margin: '0 0 10px 22px', padding: 0, fontSize: '14px' },
  group: { marginBottom: '14px', minWidth: 0 },
  row: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '15px' },
  label: { display: 'block', marginBottom: '5px', fontWeight: 'bold', color: 'var(--text-strong)', fontSize: '14px' },
  hint: { fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic', fontWeight: 'normal', marginTop: '4px' },
  input: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid var(--border-color)',
    borderRadius: '4px',
    fontSize: '14px',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    background: '#fff',
  },
  readonly: { background: 'var(--surface-muted)', color: 'var(--text-muted)' },
  choice: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    cursor: 'pointer',
    padding: '14px',
    border: '1px solid var(--border-color)',
    borderLeft: '4px solid var(--border-color)',
    borderRadius: '6px',
    background: '#fff',
    fontSize: '14px',
    marginBottom: '12px',
  },
  choiceSelected: { borderLeftColor: 'var(--primary-color)', background: '#f0f8ff' },
  radio: { marginTop: '3px', transform: 'scale(1.3)', flexShrink: 0, accentColor: '#2a5885' },
  releaseDetails: {
    margin: '0 0 12px 24px',
    padding: '14px 16px',
    borderLeft: '3px solid var(--primary-color)',
    background: '#fff',
    borderRadius: '0 6px 6px 0',
  },
  checkboxList: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' },
  checkboxItem: {
    display: 'flex',
    alignItems: 'flex-start',
    cursor: 'pointer',
    padding: '10px 12px',
    border: '1px solid var(--border-color)',
    borderRadius: '6px',
    background: 'var(--surface-muted)',
  },
  checkbox: { marginRight: '12px', marginTop: '3px', transform: 'scale(1.3)', flexShrink: 0, accentColor: '#2a5885' },
  checkboxText: { lineHeight: '1.5', fontSize: '14px', color: 'var(--text-strong)', fontWeight: '500' },
  signatureSection: {
    marginTop: '14px',
    padding: '16px 18px',
    border: '2px solid #2a5885',
    borderRadius: '8px',
    background: 'linear-gradient(135deg, #f8f9ff 0%, #f0f8ff 100%)',
  },
  signatureLabel: { display: 'block', fontWeight: 'bold', color: 'var(--primary-color)', marginBottom: '10px', fontSize: '15px' },
  signatureContainer: {
    border: '3px solid #2a5885',
    borderRadius: '8px',
    margin: '6px 0 10px',
    display: 'inline-block',
    maxWidth: '100%',
    background: 'white',
    padding: '10px',
    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1), 0 4px 8px rgba(42, 88, 133, 0.1)',
  },
  signatureCanvas: { borderRadius: '4px', display: 'block', maxWidth: '100%' },
  signatureInstructions: {
    textAlign: 'center',
    color: 'var(--text-muted)',
    fontStyle: 'italic',
    fontSize: '12px',
    marginTop: '5px',
    padding: '5px',
    background: 'var(--surface-muted)',
    borderRadius: '4px',
  },
  clearButton: {
    background: 'var(--error-color)',
    color: 'white',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
    marginBottom: '12px',
  },
  errorText: { color: 'var(--error-color)', fontSize: '13px', marginTop: '4px', fontWeight: 'bold' },
  submitError: {
    color: 'var(--error-color)',
    background: '#f8d7da',
    border: '1px solid #f5c6cb',
    borderRadius: '4px',
    padding: '10px 14px',
    marginBottom: '12px',
    fontSize: '14px',
  },
  navigationButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '10px',
    paddingTop: '20px',
    borderTop: '2px solid var(--border-color)',
  },
  submitButton: {
    padding: '12px 24px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold',
    background: 'var(--success-color)',
    color: 'white',
  },
  printButton: {
    padding: '12px 24px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold',
    background: '#17a2b8',
    color: 'white',
  },
  centered: { textAlign: 'center', padding: '40px 20px', maxWidth: '600px', margin: '0 auto' },
  errorMessage: { color: 'var(--error-color)', fontSize: '16px', marginBottom: '20px' },
  retryButton: {
    background: '#2a5885',
    color: 'white',
    border: 'none',
    padding: '12px 24px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '16px',
  },
};
